/**
 * The realtime client.
 *
 * One socket per room, for the whole life of the tab. Responsibilities:
 *
 *  - connect to `/ws?room=CODE` and send `join` (replaying the stored session
 *    token, SPEC §5.4) as the very first frame;
 *  - validate **every** inbound frame against the protocol schema before it is
 *    allowed anywhere near the store — the boundary is the only place a bad
 *    payload can be caught cheaply;
 *  - reconnect automatically with exponential backoff and jitter, re-joining
 *    with the same token so the seat is silently reclaimed and the server's
 *    snapshot replaces local state wholesale;
 *  - queue outbound intents fired while the socket is briefly down, so a tap
 *    during a blip is not lost.
 *
 * It owns no game state. The store subscribes to it.
 */
import {
  WS_PATH,
  encodeClientMessage,
  parseServerMessage,
  type ClientMessage,
  type ServerMessage,
} from '@idgame/protocol';
import {
  clearSession,
  loadHostToken,
  loadSessionToken,
  saveSession,
  touchSession,
} from './session.js';

export type ConnectionStatus =
  /** Never connected, or explicitly closed. */
  | 'idle'
  /** First connection attempt for this room. */
  | 'connecting'
  /** Live. */
  | 'open'
  /** Dropped; retrying. The user should see, at most, a subtle hint. */
  | 'reconnecting'
  /** Given up, or the server closed us out fatally. */
  | 'closed';

export interface SocketEvents {
  /** A validated server frame. */
  message: (message: ServerMessage) => void;
  status: (status: ConnectionStatus, detail: StatusDetail) => void;
  /**
   * A frame that failed schema validation. Never reaches `message`.
   *
   * The second argument is the frame's `t` discriminator only — **never the
   * payload**. A malformed `player_photo` carries a real face (SPEC §6).
   */
  protocolError: (error: string, discriminator: string) => void;
}

export interface StatusDetail {
  /** How many consecutive failed attempts. Zero once connected. */
  attempt: number;
  /** ms until the next attempt, when status is 'reconnecting'. */
  retryInMs: number | null;
  /** Close code / reason, when we have one. */
  reason: string | null;
}

export interface GameSocketOptions {
  roomCode: string;
  /** Override for tests. Defaults to the real global. */
  webSocketImpl?: typeof WebSocket;
  /** Override for tests / dev proxies. Defaults to the current origin. */
  urlFor?: (roomCode: string) => string;
  /** Backoff knobs, exposed for tests. */
  backoff?: Partial<BackoffOptions>;
  /** Keepalive interval. Zero disables it. */
  heartbeatMs?: number;
  /** Injectable clock, for tests. */
  now?: () => number;
  /**
   * The turn index the store currently believes is live, or null outside a
   * turn. Queued turn-scoped intents are stamped with this at enqueue time and
   * **discarded** if the room has moved on by the time they would be flushed —
   * see `#flushQueue`. Without it, a prediction made on turn 1 can be accepted
   * as the player's turn-2 prediction, which is not stale UI but corrupted
   * authoritative input.
   */
  currentTurnIndex?: () => number | null;
}

export interface BackoffOptions {
  baseMs: number;
  factor: number;
  maxMs: number;
  /** 0 = deterministic, 1 = full jitter. */
  jitter: number;
  /** Give up after this many consecutive failures. Infinity to retry forever. */
  maxAttempts: number;
}

const DEFAULT_BACKOFF: BackoffOptions = {
  baseMs: 300,
  factor: 1.8,
  maxMs: 10_000,
  jitter: 0.5,
  // A phone that has been in a pocket for ten minutes should still come back.
  maxAttempts: Infinity,
};

const DEFAULT_HEARTBEAT_MS = 25_000;

/** Default websocket URL: same origin as the SPA, so no CORS and no config. */
export function defaultSocketUrl(roomCode: string): string {
  const loc = globalThis.location;
  const protocol = loc && loc.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = loc?.host ?? '127.0.0.1:8787';
  return `${protocol}//${host}${WS_PATH}?room=${encodeURIComponent(roomCode)}`;
}

/**
 * Intents whose meaning is bound to a specific turn. Replaying one of these
 * into a later turn would silently change the authoritative record — and,
 * because the reveal attributes predictions by name, would publicly credit a
 * player with a guess they made about a different question.
 */
const TURN_SCOPED: ReadonlySet<ClientMessage['t']> = new Set([
  'submit_prediction',
  // A lock is an answer to one turn's question, and it is one-way: replayed
  // into a later turn it would either be refused for having nothing to lock or,
  // worse, close a question the player has not finished looking at.
  'lock_prediction',
  'submit_ranking',
  'select_question',
  'thumbs_vote',
  'justification_done',
]);

/** Queued intents of these types are merged rather than replaced. */
const MERGEABLE: ReadonlySet<ClientMessage['t']> = new Set(['set_profile', 'update_settings']);

/** Only one of these can usefully be in flight; the newest wins. */
const COLLAPSIBLE: ReadonlySet<ClientMessage['t']> = new Set([
  'submit_prediction',
  // Idempotent server-side, but there is no point queueing two of them, and
  // collapsing keeps the lock behind the last pair it is locking.
  'lock_prediction',
  'submit_ranking',
  'select_question',
  'thumbs_vote',
]);

interface QueuedMessage {
  message: ClientMessage;
  /** Turn index when this was queued, or null if it was not turn-scoped. */
  turnIndex: number | null;
}

export class GameSocket {
  readonly roomCode: string;

  #options: Required<Omit<GameSocketOptions, 'backoff' | 'roomCode'>> & {
    backoff: BackoffOptions;
  };
  #ws: WebSocket | null = null;
  #status: ConnectionStatus = 'idle';
  #attempt = 0;
  #retryTimer: ReturnType<typeof setTimeout> | null = null;
  #heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  #stopped = true;
  /**
   * True once the server has answered this connection's `join` with a
   * `welcome` or `snapshot`. Nothing queued is sent before this, and the
   * backoff counter is not reset before it either.
   */
  #reclaimed = false;
  /**
   * The seat, held in memory for the life of the socket. Kept so a seat can be
   * re-persisted after `game_reset` even though `game_ended` wipes the stored
   * copy (SPEC §6 — no durable personal data once the game is over).
   */
  #seat: { sessionToken: string; playerId: string } | null = null;
  /** Intents fired while the socket was down, replayed in order after reclaim. */
  #queue: QueuedMessage[] = [];
  #listeners: { [K in keyof SocketEvents]: Set<SocketEvents[K]> } = {
    message: new Set(),
    status: new Set(),
    protocolError: new Set(),
  };

  constructor(options: GameSocketOptions) {
    this.roomCode = options.roomCode.trim().toUpperCase();
    this.#options = {
      webSocketImpl: options.webSocketImpl ?? (globalThis.WebSocket as typeof WebSocket),
      urlFor: options.urlFor ?? defaultSocketUrl,
      heartbeatMs: options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS,
      now: options.now ?? (() => Date.now()),
      currentTurnIndex: options.currentTurnIndex ?? (() => null),
      backoff: { ...DEFAULT_BACKOFF, ...options.backoff },
    };
  }

  /** True once this connection's seat has been confirmed by the server. */
  get reclaimed(): boolean {
    return this.#reclaimed;
  }

  /** How many intents are waiting for a reclaimed connection. */
  get queueLength(): number {
    return this.#queue.length;
  }

  get status(): ConnectionStatus {
    return this.#status;
  }

  get attempt(): number {
    return this.#attempt;
  }

  /** True while a reconnect is pending — the UI should stay on the last state. */
  get isRetrying(): boolean {
    return this.#status === 'reconnecting';
  }

  on<K extends keyof SocketEvents>(event: K, listener: SocketEvents[K]): () => void {
    this.#listeners[event].add(listener);
    return () => {
      this.#listeners[event].delete(listener);
    };
  }

  /** Open the socket. Idempotent. */
  connect(): void {
    this.#stopped = false;
    if (this.#ws || this.#retryTimer) return;
    this.#open(this.#attempt === 0 ? 'connecting' : 'reconnecting');
  }

  /** Close for good. No further reconnects until `connect()` is called again. */
  close(code = 1000, reason = 'client closed'): void {
    this.#stopped = true;
    this.#reclaimed = false;
    this.#clearRetry();
    this.#stopHeartbeat();
    const ws = this.#ws;
    this.#ws = null;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        if (ws.readyState === 0 || ws.readyState === 1) ws.close(code, reason);
      } catch {
        /* already gone */
      }
    }
    this.#setStatus('idle', { attempt: 0, retryInMs: null, reason });
  }

  /**
   * Send an intent. If the socket is momentarily down — or up but not yet
   * reclaimed — the message is queued and replayed once the server has
   * confirmed the seat, which is what makes a blip invisible. `join` is never
   * queued; the reconnect path always sends a fresh one.
   */
  send(message: ClientMessage): void {
    const ws = this.#ws;
    // `claim_seat` is how a seat is obtained, so waiting for one is circular:
    // it is the answer to `claimable_seats`, which arrives instead of a
    // welcome. It goes out on an open socket regardless of `#reclaimed`.
    const beforeSeat = message.t === 'claim_seat';
    if (ws && ws.readyState === 1 && (this.#reclaimed || beforeSeat)) {
      try {
        ws.send(encodeClientMessage(message));
        return;
      } catch {
        /* fall through to the queue */
      }
    }
    // Neither survives being replayed later: the socket that was offered those
    // seats is gone, and the reconnect sends a fresh `join` which gets a fresh
    // offer.
    if (message.t === 'join' || beforeSeat) return;
    this.#enqueue(message);
    if (!this.#stopped) this.connect();
  }

  /**
   * Answer a `claimable_seats` offer: take the named seat, or ask for a new
   * one (SPEC §5.4).
   *
   * Here rather than in the store because the host token belongs to this
   * layer — the same replay `#sendJoin` does. The device that created the room
   * can perfectly well be the one whose storage was wiped, and the seat it
   * points at is still the host seat (SPEC §5.5).
   */
  claimSeat(playerId?: string): void {
    const hostToken = loadHostToken(this.roomCode);
    this.send({
      t: 'claim_seat',
      ...(playerId ? { playerId } : {}),
      ...(hostToken ? { hostToken } : {}),
    });
  }

  /** Drop everything waiting to be sent. Used when a seat is abandoned. */
  clearQueue(): void {
    this.#queue = [];
  }

  /* ------------------------------------------------------------------ */
  /* Internals                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Queue an intent for replay after the seat is reclaimed.
   *
   * Three behaviours, and the difference between them matters:
   *
   *  - **Collapsed** (predictions, rankings, votes): only the newest is useful.
   *    A predictor flicking between three candidates while offline should send
   *    one prediction on reconnect, not three.
   *  - **Merged** (`set_profile`, `update_settings`): fields are combined. These
   *    are sent in parts — the photo from the capture screen, the name from the
   *    name screen — and collapsing would silently discard the photo while the
   *    client believed the profile was complete.
   *  - **Appended** (everything else).
   */
  #enqueue(message: ClientMessage): void {
    // `ping` is pointless once stale.
    if (message.t === 'ping') return;

    const turnIndex = TURN_SCOPED.has(message.t) ? this.#options.currentTurnIndex() : null;

    if (MERGEABLE.has(message.t)) {
      const existing = this.#queue.find((queued) => queued.message.t === message.t);
      if (existing) {
        existing.message = { ...existing.message, ...message } as ClientMessage;
        existing.turnIndex = turnIndex;
        return;
      }
    } else if (COLLAPSIBLE.has(message.t)) {
      this.#queue = this.#queue.filter((queued) => queued.message.t !== message.t);
    }

    this.#queue.push({ message, turnIndex });
  }

  #open(status: ConnectionStatus): void {
    const Impl = this.#options.webSocketImpl;
    if (typeof Impl !== 'function') {
      this.#setStatus('closed', {
        attempt: this.#attempt,
        retryInMs: null,
        reason: 'websockets unavailable',
      });
      return;
    }

    this.#setStatus(status, { attempt: this.#attempt, retryInMs: null, reason: null });

    let ws: WebSocket;
    try {
      ws = new Impl(this.#options.urlFor(this.roomCode));
    } catch (error) {
      this.#scheduleRetry(String((error as Error)?.message ?? error));
      return;
    }

    this.#ws = ws;

    ws.onopen = () => {
      if (this.#ws !== ws) return;
      // The transport being up is not the same as having a seat. The attempt
      // counter is *not* reset here: a proxy that accepts the upgrade and then
      // closes before `welcome` would otherwise retry at the base delay
      // forever instead of backing off. Reset happens in `#onReclaimed`.
      this.#reclaimed = false;
      this.#setStatus('open', {
        attempt: this.#attempt,
        retryInMs: null,
        reason: null,
      });
      this.#sendJoin(ws);
      this.#startHeartbeat();
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.#ws !== ws) return;
      this.#handleFrame(event.data);
    };

    ws.onerror = () => {
      /* `onclose` always follows; retry logic lives there. */
    };

    ws.onclose = (event: CloseEvent) => {
      if (this.#ws !== ws) return;
      this.#ws = null;
      this.#reclaimed = false;
      this.#stopHeartbeat();
      if (this.#stopped) return;
      this.#scheduleRetry(event.reason || `closed (${event.code})`);
    };
  }

  /** The first frame on every connection, new or resumed (SPEC §5.4). */
  #sendJoin(ws: WebSocket): void {
    const sessionToken = loadSessionToken(this.roomCode);
    // Only this device has one, and only for the room it created (SPEC §5.5).
    // Sent on every join, not just the first: the seat it claims is the host
    // seat, and the server ignores it once it has been spent.
    const hostToken = loadHostToken(this.roomCode);
    const join: ClientMessage = {
      t: 'join',
      roomCode: this.roomCode,
      ...(sessionToken ? { sessionToken } : {}),
      ...(hostToken ? { hostToken } : {}),
    };
    try {
      ws.send(encodeClientMessage(join));
    } catch {
      /* onclose will pick it up */
    }
  }

  /**
   * Called once the server has answered `join` with `welcome`/`snapshot`, and
   * — critically — **after** the store has applied that frame, so the turn
   * index below is the room's current one rather than the one we left on.
   */
  #onReclaimed(): void {
    if (this.#reclaimed) return;
    this.#reclaimed = true;
    this.#attempt = 0;
    this.#setStatus('open', { attempt: 0, retryInMs: null, reason: null });

    const ws = this.#ws;
    if (ws && ws.readyState === 1) this.#flushQueue(ws);
  }

  /**
   * Replay queued intents, dropping any turn-scoped one whose turn the room has
   * already left. A prediction is an answer to a specific question; delivering
   * it against a different one is worse than losing it.
   */
  #flushQueue(ws: WebSocket): void {
    if (this.#queue.length === 0) return;

    const currentTurn = this.#options.currentTurnIndex();
    const pending = this.#queue;
    this.#queue = [];

    for (const queued of pending) {
      if (queued.turnIndex !== null && queued.turnIndex !== currentTurn) continue;
      try {
        ws.send(encodeClientMessage(queued.message));
      } catch {
        this.#queue.push(queued);
      }
    }
  }

  #handleFrame(data: unknown): void {
    const result = parseServerMessage(data);
    if (!result.ok) {
      // Only the schema error and the discriminator. A malformed `player_photo`
      // carries a real face as a data URL, and console logs outlive the frame
      // they came from (SPEC §6).
      this.#emit('protocolError', result.error, discriminatorOf(data));
      return;
    }

    const message = result.value;

    // Session lifecycle belongs to the socket layer rather than the store.
    switch (message.t) {
      case 'welcome':
        this.#seat = { sessionToken: message.sessionToken, playerId: message.selfId };
        saveSession({
          roomCode: this.roomCode,
          sessionToken: message.sessionToken,
          playerId: message.selfId,
        });
        break;

      case 'snapshot':
      case 'phase_changed':
        touchSession(this.roomCode);
        break;

      case 'game_ended':
        // The server purges the room; the device should not keep a seat for it
        // either (SPEC §6). `#seat` survives in memory so "play again" can
        // re-persist it below without a round trip.
        clearSession(this.roomCode);
        break;

      case 'game_reset':
        // Play again: same room, same seat. Restore what `game_ended` cleared.
        if (this.#seat) saveSession({ roomCode: this.roomCode, ...this.#seat });
        break;

      case 'error':
        if (message.fatal) {
          if (message.code === 'room_not_found' || message.code === 'game_in_progress') {
            clearSession(this.roomCode);
          }
          this.#stopped = true;
          this.#clearRetry();
        }
        break;

      default:
        break;
    }

    this.#emit('message', message);

    // After the listeners have run, so the store's snapshot — and therefore the
    // turn index the queue is filtered against — is already current.
    if (message.t === 'welcome' || message.t === 'snapshot') this.#onReclaimed();
  }

  #scheduleRetry(reason: string | null): void {
    this.#clearRetry();
    this.#attempt += 1;

    const { maxAttempts } = this.#options.backoff;
    if (this.#attempt > maxAttempts) {
      this.#setStatus('closed', { attempt: this.#attempt, retryInMs: null, reason });
      return;
    }

    const delay = this.backoffDelay(this.#attempt);
    this.#setStatus('reconnecting', { attempt: this.#attempt, retryInMs: delay, reason });
    this.#retryTimer = setTimeout(() => {
      this.#retryTimer = null;
      if (this.#stopped) return;
      this.#open('reconnecting');
    }, delay);
  }

  /** Exponential with full-jitter, capped. Exposed so tests can assert shape. */
  backoffDelay(attempt: number): number {
    const { baseMs, factor, maxMs, jitter } = this.#options.backoff;
    const raw = Math.min(maxMs, baseMs * Math.pow(factor, Math.max(0, attempt - 1)));
    if (jitter <= 0) return Math.round(raw);
    const floor = raw * (1 - jitter);
    return Math.round(floor + Math.random() * (raw - floor));
  }

  #clearRetry(): void {
    if (this.#retryTimer !== null) {
      clearTimeout(this.#retryTimer);
      this.#retryTimer = null;
    }
  }

  #startHeartbeat(): void {
    this.#stopHeartbeat();
    const interval = this.#options.heartbeatMs;
    if (!interval) return;
    this.#heartbeatTimer = setInterval(() => {
      const ws = this.#ws;
      if (!ws || ws.readyState !== 1) return;
      try {
        ws.send(encodeClientMessage({ t: 'ping', sentAt: this.#options.now() }));
      } catch {
        /* onclose will handle it */
      }
    }, interval);
  }

  #stopHeartbeat(): void {
    if (this.#heartbeatTimer !== null) {
      clearInterval(this.#heartbeatTimer);
      this.#heartbeatTimer = null;
    }
  }

  #setStatus(status: ConnectionStatus, detail: StatusDetail): void {
    this.#status = status;
    this.#emit('status', status, detail);
  }

  #emit<K extends keyof SocketEvents>(event: K, ...args: Parameters<SocketEvents[K]>): void {
    for (const listener of this.#listeners[event]) {
      (listener as (...a: Parameters<SocketEvents[K]>) => void)(...args);
    }
  }
}

/**
 * The `t` field of a rejected frame, and nothing else — enough to debug a
 * protocol mismatch, not enough to leak a payload.
 */
function discriminatorOf(raw: unknown): string {
  try {
    const value: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (value && typeof value === 'object' && typeof (value as { t?: unknown }).t === 'string') {
      return (value as { t: string }).t;
    }
  } catch {
    /* not JSON */
  }
  return '<unparseable>';
}
